import gaSC from '@common/utils/ga';
import { request, apiBase } from '@common/request';
import {
  setCouponCidCookie,
  getOrCreateSessionId,
  couponPath,
  redeemPath,
} from '@common/utils/coupon-cid';
import { copyToClipboard } from '@common/utils/clipboard';
import { storeDynamicCode } from '@common/utils/dynamic-code';

/**
 * Coupon click — every `.go-cpn-show` on every page.
 *
 * The .in engine's version of this file is a 390-line switch over eight click
 * types (cp/dl/exp/cb/dc/cs/ds/exps), each opening some combination of
 * `redeem/<cid>/`, `load/coupon/?go=<cid>` and `load/cv/...`. Of those, only
 * the redeem interstitial has been built here (see RedeemController); the load
 * endpoints are .NET tracking routes with no International counterpart. So the
 * switch is not ported — eight branches would resolve to two destinations.
 *
 * The flow is International's, from `couponActivation.ts` + `CouponCard.tsx`,
 * with .in's two-tab split laid over it:
 *
 *   1. POST /coupons/click  — AWAITED, because the response may carry a
 *      `dynamicCode` issued for this click and no other
 *   2. if a dynamic code came back: stash it (so the reveal page shows the same
 *      one) and copy it; otherwise set the coupon_cid cookie and copy the
 *      static code
 *   3. open `/redeem/<id>/` in a new tab — the interstitial that forwards to
 *      the merchant
 *   4. send THIS tab to `/coupon-codes/<id>/`
 *
 * Both tabs are deliberate: the new one activates the offer on the merchant
 * site, this one shows the code.
 *
 * THE ONE PLACE WE DIVERGE, AND WHY. Next.js awaits the POST and only then
 * calls `window.open`. By that point the user-gesture task is over, so Safari
 * and Firefox block the tab — the visitor gets tracking and no offer. .in
 * dodges this with a SYNCHRONOUS XMLHttpRequest, which freezes the tab for the
 * length of a network round trip. Neither is acceptable, so: the tab is opened
 * empty inside the gesture and pointed at the URL once the response lands. That
 * is the standard fix and changes nothing the visitor can observe, except that
 * it works.
 */
export function couponClickInit() {
  document.addEventListener('click', (event) => {
    const el = event.target.closest('.go-cpn-show');
    if (!el) return;

    const couponId = el.getAttribute('data-goid');
    // No coupon id — a plain banner or deal. go-redirection owns those.
    if (!couponId) return;

    event.preventDefault();
    handleClick(el, couponId);
  });
}

function handleClick(el, couponId) {
  const target = couponPath(couponId, false);
  if (!target) return;
  // The new tab goes to the redeem interstitial; this tab shows the code. Same
  // split as .in — its onCouponClick(redeemCodeURL, couponCodeURL).
  const redeemTarget = redeemPath(couponId);

  // Opened NOW, empty, while the gesture is still live. See the header comment.
  //
  // NO `noopener` IN THE FEATURES STRING. With it, the spec requires
  // window.open to return null — the whole point is to deny the opener a
  // handle on the new window. We need that handle to navigate the tab once the
  // POST answers, so the relationship is severed the other way instead:
  // `tab.opener = null` below, which gives the same protection and still
  // leaves us the reference. Passing 'noopener' here left every visitor with a
  // permanently blank tab and no redemption.
  const tab = window.open('', '_blank');

  // .in: gaSC(`${category} : ${label}`, label, 0, id) — the coupon id rides as
  // dimension3 on the page_view, not as the event label.
  const ctype = el.getAttribute('data-ctype');
  gaSC(gaLabel(ctype), (ctype && GA_LABELS[ctype]) || ctype || '', 0, couponId);

  const staticCode = el.getAttribute('data-code') || '';

  // Everything after this must run whether the POST succeeded, failed or was
  // never made: a lost statistic must not cost the visitor their coupon.
  const go = (dynamicCode) => {
    if (dynamicCode) {
      storeDynamicCode(couponId, dynamicCode);
      copyToClipboard(dynamicCode);
    } else {
      setCouponCidCookie(couponId, el.getAttribute('data-cid') || couponId);
      if (staticCode) copyToClipboard(staticCode);
    }

    if (tab) {
      // Severed here rather than via the features string — see above.
      try {
        tab.opener = null;
      } catch {
        /* cross-origin about:blank in some engines; the navigation still works */
      }
      tab.location.href = redeemTarget;
    }
    // A blocked popup must not cost the visitor the page entirely — without the
    // tab, this one still goes to the reveal page.
    window.location.href = target;
  };

  if (!apiBase()) {
    go(null);
    return;
  }

  trackClick(el, couponId)
    .then((data) => go(dynamicCodeOf(data)))
    .catch(() => go(null));
}

/**
 * POST /coupons/click — payload from lib/api/coupons.ts `CouponClickData`.
 * Resolves with the response so the caller can read a dynamic code out of it.
 */
function trackClick(el, couponId) {
  return request.post(`${apiBase()}/coupons/click`, {
    couponId,
    // `categoryId` is the merchant id: the Next.js caller passes `merchantId`
    // into that field. Named for the backend, not for the DOM.
    categoryId: el.getAttribute('data-catid') || undefined,
    sessionId: getOrCreateSessionId() || undefined,
    pageUrl: window.location.href,
    referrer: document.referrer || '',
    userAgent: navigator.userAgent || '',
    deviceType: deviceType(),
  });
}

/**
 * The dynamic code, wherever the backend put it.
 *
 * `normalizeDynamicCodeValue` in the Next.js client accepts a bare string, a
 * `{dynamicCode}` object and a `{data:{...}}` envelope, which says the response
 * shape is not settled. Reading all three is cheaper than being wrong.
 */
function dynamicCodeOf(data) {
  if (!data) return null;
  if (typeof data === 'string') return data.trim() || null;
  const value = data.dynamicCode ?? data.code ?? data.data?.dynamicCode ?? data.data?.code;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function deviceType() {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * `data-ctype` → a readable GA label. The codes are .in's and they travel with
 * the migrated markup, so the mapping comes across even though the click flow
 * does not. Anything unlisted reports its raw code rather than "Misc", which is
 * what .in does — a code in the report can at least be traced to a template.
 */
const GA_LABELS = {
  cid_m: 'Coupon Button in cid page',
  cid_r_c: 'Related Coupon in cid page',
  sc_c: 'Suggested Coupon',
  cc_c: 'Coupon Button in listing pages',
  rc_c: 'Related Coupon in listing pages',
  ec_c: 'Expired Coupons in listing pages',
  tab_b_h: 'Home Page Tab Banner',
  tab_c_h: 'Home Page Todays Top Coupons',
  gc_h: 'GrabOn Collections Coupons',
  search_r_c: 'Search Result Page Coupons',
  search_nr_c: 'Search Result Hottest Now Coupons',
  'cpn-u-lk': 'Coupons You May Like',
  bl_c: 'Bell Notifications Coupon Clicked',
};

function gaLabel(ctype) {
  if (!ctype) return 'Coupon Click';
  return `Coupon Click : ${GA_LABELS[ctype] || ctype}`;
}
